import { useEffect, useState } from 'react';
import { BookOpen, AlertCircle, Plus, Info, Workflow } from 'lucide-react';
import { Badge, Button, Card, CardBody, CardHeader, CardTitle, Input } from '@solara/ui-shared';
import { useTranslation } from '@solara/i18n';
import { accountingApi, ApiError } from '@solara/api-client';
import type { GlAccountDto } from '@solara/api-client';

interface PostingTemplateRow {
  id: string;
  name: string;
  eventType: string;
  debitAccountId: string;
  creditAccountId: string;
  priority: number;
  isActive: boolean;
}

const ACCOUNT_TYPES = ['Asset', 'Liability', 'Equity', 'Income', 'Expense'] as const;

export default function GlAccountsPage() {
  const { t } = useTranslation('p1');
  const [accounts, setAccounts] = useState<GlAccountDto[]>([]);
  const [templates, setTemplates] = useState<PostingTemplateRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<{
    code: string;
    name: string;
    accountType: (typeof ACCOUNT_TYPES)[number];
    accountSubType: string;
    currency: string;
  }>({
    code: '',
    name: '',
    accountType: 'Asset',
    accountSubType: '',
    currency: 'AED',
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    try {
      const [acc, tmpl] = await Promise.all([
        accountingApi.glAccounts(),
        accountingApi.postingTemplates().catch(() => [] as unknown[]),
      ]);
      setAccounts(acc);
      setTemplates(tmpl as PostingTemplateRow[]);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    }
  }

  const accountByCode = new Map(accounts.map((a) => [a.id, a]));

  async function add() {
    if (!draft.code || !draft.name) {
      setError(t('common.fieldRequired'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await accountingApi.createGlAccount({
        code: draft.code,
        name: draft.name,
        accountType: draft.accountType,
        accountSubType: draft.accountSubType || undefined,
        currency: draft.currency,
      });
      setDraft({ code: '', name: '', accountType: 'Asset', accountSubType: '', currency: 'AED' });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  // Group by accountType for a basic tree display.
  const grouped = ACCOUNT_TYPES.map((type) => ({
    type,
    items: accounts.filter((a) => a.accountType === type),
  })).filter((g) => g.items.length > 0);

  return (
    <div className="space-y-6 max-w-5xl">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t('accounting.glTitle')}</h1>
        <p className="text-fg-muted text-sm">{t('accounting.glSubtitle')}</p>
      </header>

      {error && (
        <Card className="border-danger/40 bg-danger/5 rounded-2xl">
          <CardBody className="flex items-center gap-2 text-sm text-danger">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </CardBody>
        </Card>
      )}

      <Card className="rounded-2xl border-info/40 bg-info/5">
        <CardBody className="text-sm space-y-2">
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 mt-0.5 text-info shrink-0" />
            <div className="space-y-1">
              <p className="font-medium">
                {t('accounting.glScopeTitle', {
                  defaultValue: 'Chart of accounts is per-tenant',
                })}
              </p>
              <p className="text-fg-muted">
                {t('accounting.glScopeBody', {
                  defaultValue:
                    'Every tenant owns its own chart of accounts. New tenants are seeded from the default Solara template (UAE FRS) but can be customised independently.',
                })}
              </p>
              <p className="text-fg-muted">
                {t('accounting.glAutoPostingBody', {
                  defaultValue:
                    'Auto-posting is driven by Posting Templates below: each template maps an event (invoice.delivered, bank.received, …) to a debit/credit account pair with optional filters (VAT category, product category, counterparty).',
                })}
              </p>
            </div>
          </div>
        </CardBody>
      </Card>

      {grouped.map((group) => (
        <Card key={group.type} className="rounded-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              {t('accounting.typeCount', {
                type: t(`accounting.types.${group.type}`, { defaultValue: group.type }),
                count: group.items.length,
              })}
            </CardTitle>
          </CardHeader>
          <CardBody>
            <ul className="divide-y divide-border text-sm">
              {group.items.map((a) => (
                <li key={a.id} className="flex items-center justify-between py-2">
                  <div>
                    <p className="font-medium font-mono">
                      {a.code} <span className="text-fg-muted font-sans">— {a.name}</span>
                    </p>
                    {a.accountSubType && <p className="text-xs text-fg-muted">{a.accountSubType}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge tone="neutral" className="text-[10px]">{a.currency}</Badge>
                    {!a.isPostable && (
                      <Badge tone="warning" className="text-[10px]">{t('accounting.summary')}</Badge>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      ))}

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Workflow className="h-4 w-4" />
            {t('accounting.postingTemplatesTitle', {
              defaultValue: 'Auto-posting templates',
            })}
            <Badge tone="neutral" className="text-[10px]">{templates.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardBody>
          {templates.length === 0 ? (
            <p className="text-sm text-fg-muted">
              {t('accounting.noPostingTemplates', {
                defaultValue:
                  'No posting templates configured. Outgoing/incoming invoices and bank movements fall back to manual journal entries until templates are defined.',
              })}
            </p>
          ) : (
            <ul className="divide-y divide-border text-sm">
              {templates.map((tmpl) => {
                const debit = accountByCode.get(tmpl.debitAccountId);
                const credit = accountByCode.get(tmpl.creditAccountId);
                return (
                  <li key={tmpl.id} className="flex items-center justify-between gap-2 py-2">
                    <div>
                      <p className="font-medium">{tmpl.name}</p>
                      <p className="text-xs text-fg-muted">
                        <span className="font-mono">{tmpl.eventType}</span>
                        {' · DR '}
                        <span className="font-mono">{debit?.code ?? tmpl.debitAccountId.slice(0, 8)}</span>
                        {' · CR '}
                        <span className="font-mono">{credit?.code ?? tmpl.creditAccountId.slice(0, 8)}</span>
                      </p>
                    </div>
                    <Badge tone={tmpl.isActive ? 'success' : 'neutral'} className="text-[10px]">
                      {tmpl.isActive
                        ? t('common.active', { defaultValue: 'Active' })
                        : t('common.inactive', { defaultValue: 'Inactive' })}
                    </Badge>
                  </li>
                );
              })}
            </ul>
          )}
        </CardBody>
      </Card>

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            {t('accounting.newAccount')}
          </CardTitle>
        </CardHeader>
        <CardBody className="grid grid-cols-1 gap-3 sm:grid-cols-2 text-sm">
          <Input
            label={t('accounting.codeHint')}
            value={draft.code}
            onChange={(e) => setDraft({ ...draft, code: e.target.value })}
          />
          <Input
            label={t('accounting.accountName')}
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
          <select
            className="rounded border border-border bg-surface px-3 py-2 text-sm"
            value={draft.accountType}
            onChange={(e) => setDraft({ ...draft, accountType: e.target.value as typeof draft.accountType })}
          >
            {ACCOUNT_TYPES.map((ty) => (
              <option key={ty} value={ty}>
                {t(`accounting.types.${ty}`, { defaultValue: ty })}
              </option>
            ))}
          </select>
          <Input
            label={t('accounting.accountSubType')}
            value={draft.accountSubType}
            onChange={(e) => setDraft({ ...draft, accountSubType: e.target.value })}
          />
          <Input
            label={t('accounting.currency')}
            value={draft.currency}
            onChange={(e) => setDraft({ ...draft, currency: e.target.value.toUpperCase() })}
          />
          <div className="sm:col-span-2">
            <Button onClick={add} disabled={busy}>
              {busy ? t('actions.saving') : t('accounting.createAccount')}
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
